import AppHeader from './components/AppHeader';
import { I18nProvider } from '@cloudscape-design/components/i18n';
import messages from '@cloudscape-design/components/i18n/messages/all.en';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { SplitPanelProvider } from './context/SplitPanelContext';
import { NavigationProvider } from './context/NavigationContext';
import AppLayoutWrapper from './components/AppLayoutWrapper';
import { useUsers } from './store/users/useUsers';
import useUserProfile from './hooks/useUserProfile';
import { useEffect, useRef, useState } from 'react';
import { Flashbar, FlashbarProps, Link } from '@cloudscape-design/components';
import { useNavigate, useLocation } from 'react-router-dom';
import routePaths from './contstant/routePaths';
import useMidway from './hooks/useMidway';
import { LOCAL_STORAGE_KEYS } from './contstant/commonLabels';
import { saveToLocalStorage } from './utils/localStorageUtils';
import { useUserPreferences } from './store/userPreferences/useUserPreferences';
import {
  CopilotProvider,
  CopilotTrigger,
  CopilotPanel,
  isCopilotHiddenOnRoute,
} from './components/copilot';
import { useBusinessUnit } from './store/businessUnit/useBusinessUnit';
import { AuthHandler } from './components/auth/AuthHandler';
import AILoadingScreen from './components/AILoadingScreen';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import UserProvider from 'src/providers/UserProvider';
import { WhatsNewBanner } from './components/home/WhatsNewBanner';
import { initializeRum, getRumInstance } from './rum';
import { useRumPageTracking } from './hooks/useRumPageTracking';

const LOCALE = 'en';
const queryClient = new QueryClient();

// Survey configuration — update or remove after survey closes
const SURVEY_CONFIG = {
  url: 'https://admin.pulse.aws/staffSurvey/FY11I0HT',
  deadline: 'July 3rd',
  endDate: new Date('2026-07-04T00:00:00Z'),
};

// Banner content strings — edit these to update the banner.
// The localStorage dismiss key is derived from these strings, so any change
// automatically re-shows the banner to all users.
const WHATS_NEW_LINK_TEXT = "Check out What's New";
const WHATS_NEW_TEXT = `New features available! ${WHATS_NEW_LINK_TEXT} for details.`;
const SURVEY_LINK_TEXT = 'Take the Survey';
const SURVEY_TEXT = `📢 Share your feedback on Gravion → ${SURVEY_LINK_TEXT} — survey closes ${SURVEY_CONFIG.deadline}`;

const WHATS_NEW_STORAGE_KEY = `whatsNewBannerDismissed_${(WHATS_NEW_TEXT + SURVEY_TEXT).replace(/\s+/g, '')}`;

const AppContent = () => {
  const { userAlias, isError, isLoading } = useMidway();
  const navigate = useNavigate();
  const location = useLocation();

  // Track page views automatically
  useRumPageTracking();
  const { getUserByUserId, getUserMetadata } = useUsers();
  const { getUserProgramId, getUserBusinessUnitId } = useUserProfile();
  const { userPreferences, loadUserPreferences, getUserPreferencesApiStatus } =
    useUserPreferences();
  const { loadBusinessUnits } = useBusinessUnit();
  const [isAllowToCheckBU, setAllowtoCheckBU] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(() => {
    return localStorage.getItem(WHATS_NEW_STORAGE_KEY) !== 'true';
  });

  const handleDismissWhatsNew = () => {
    localStorage.setItem(WHATS_NEW_STORAGE_KEY, 'true');
    setShowWhatsNew(false);
  };

  const getWhatsNewMessage = () => {
    const [whatsNewBefore, whatsNewAfter] = WHATS_NEW_TEXT.split(WHATS_NEW_LINK_TEXT);
    const [surveyBefore, surveyAfter] = SURVEY_TEXT.split(SURVEY_LINK_TEXT);

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>
          {whatsNewBefore}
          <Link
            onFollow={() => {
              const whatsNewSection = document.getElementById('whats-new-section');
              if (whatsNewSection) {
                whatsNewSection.scrollIntoView({ behavior: 'smooth' });
              } else {
                navigate('/', { replace: true });
                setTimeout(() => {
                  const element = document.getElementById('whats-new-section');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }
            }}
          >
            {WHATS_NEW_LINK_TEXT}
          </Link>
          {whatsNewAfter}
        </span>
        {new Date() < SURVEY_CONFIG.endDate && (
          <span className="survey-feedback-container">
            <span className="survey-feedback-scroll">
              {surveyBefore}
              <Link href={SURVEY_CONFIG.url} external>
                {SURVEY_LINK_TEXT}
              </Link>
              {surveyAfter}
            </span>
          </span>
        )}
      </div>
    );
  };

  // Global listener for business unit changes across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'businessUnitChanged' && e.newValue) {
        try {
          // We do NOT clear filters on BU change
          // Filters are scoped per business unit and should persist
          // when switching between BUs

          // Full page reload is intentional here because:
          // 1. Business unit change is a major context switch
          // 2. All Redux state is scoped to business unit and becomes stale
          // 3. User permissions are business-unit specific and need to be re-evaluated
          // 4. All cached API responses are invalid for the new business unit
          // 5. This is a rare operation, so UX impact is acceptable
          // 6. Simpler and more reliable than manually resetting all Redux slices
          window.location.reload();
        } catch (error) {
          console.error('[App] Failed to parse businessUnitChanged event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Reload the *acting* tab when its business unit actually changes.
  //
  // The cross-tab `storage` listener above only reloads OTHER tabs — the Web Storage
  // spec never delivers a `storage` event to the tab that made the change. So without
  // this, the tab where the user switched the BU keeps stale, BU-scoped state (Redux,
  // cached API responses, permissions, the copilot session), and whether a reload
  // "happened" depended purely on whether another tab was open. Driving the reload off
  // the resolved BU (the source of truth) makes it deterministic and consistent with the
  // cross-tab path, for every BU-change entry point.
  const currentBusinessUnitId = getUserBusinessUnitId();
  const previousBusinessUnitIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousBusinessUnitIdRef.current;
    // Seed the starting BU on first resolution (initial load, or first-time setup where
    // the BU goes from empty to selected) without reloading — only a change between two
    // real business units is a switch.
    if (previous === null) {
      if (currentBusinessUnitId) {
        previousBusinessUnitIdRef.current = currentBusinessUnitId;
      }
      return;
    }
    if (currentBusinessUnitId && currentBusinessUnitId !== previous) {
      previousBusinessUnitIdRef.current = currentBusinessUnitId;
      window.location.reload();
    }
  }, [currentBusinessUnitId]);

  useEffect(() => {
    if (userAlias && !userPreferences) {
      saveToLocalStorage(LOCAL_STORAGE_KEYS.MIDWAY_AUTH_USERNAME, userAlias);
      loadUserPreferences({ programId: getUserProgramId(), userId: userAlias });
      setAllowtoCheckBU(true);

      // Initialize RUM and set user context
      initializeRum().then((rum) => {
        if (rum) {
          rum.addSessionAttributes({
            userId: userAlias,
            programId: getUserProgramId(),
          });
        }
      });
    }

    // Always call getUserMetadata when we have userAlias to get accurate fullName from PAPI
    if (userAlias) {
      getUserMetadata({
        userId: userAlias,
        programId: getUserProgramId(),
      });
    }

    // Only invoke getUserByUserId if userAlias has value and business unit is available
    if (userAlias && getUserBusinessUnitId()) {
      getUserByUserId({
        userId: userAlias,
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
      });
      loadBusinessUnits({
        programId: getUserProgramId(),
        isActive: true,
        isDeleted: false,
      });
    }
  }, [getUserBusinessUnitId(), userAlias]);

  // Add business unit context when available
  useEffect(() => {
    const addBusinessUnitContext = async () => {
      if (userAlias && getUserBusinessUnitId()) {
        const rum = await getRumInstance();
        if (rum) {
          rum.addSessionAttributes({
            businessUnit: getUserBusinessUnitId(),
          });
          rum.recordEvent('user_access', {
            userId: userAlias,
            programId: getUserProgramId(),
            businessUnit: getUserBusinessUnitId(),
            accessedAt: new Date().toISOString(),
          });
        }
      }
    };

    addBusinessUnitContext();
  }, [getUserBusinessUnitId(), userAlias]);

  useEffect(() => {
    if (
      !userPreferences?.profileSettings?.businessUnit &&
      !getUserPreferencesApiStatus.isLoading &&
      isAllowToCheckBU &&
      userAlias
    ) {
      navigate(routePaths.PROFILE_PREFERENCES.path);
    }
  }, [
    userPreferences,
    getUserPreferencesApiStatus.isLoading,
    location.pathname,
    isAllowToCheckBU,
    userAlias,
  ]);

  const renderContent = () => {
    const flashbarItems: FlashbarProps.MessageDefinition[] = [];

    // Allow preferences page access even without business unit
    const isOnPreferencesPage = location.pathname === routePaths.PROFILE_PREFERENCES.path;

    // Loading messages for app initialization
    const initMessages = [
      'Loading your workspace',
      'Fetching preferences',
      'Almost ready',
    ];

    // Show loading screen while midway is loading
    if (isLoading) {
      return <AILoadingScreen messages={initMessages} />;
    }

    // Show loading screen while fetching user preferences
    if (!getUserBusinessUnitId() && !isOnPreferencesPage) {
      if (getUserPreferencesApiStatus.isLoading) {
        return <AILoadingScreen messages={initMessages} />;
      }
      // If not loading and no BU, the useEffect will redirect to preferences
      return <AILoadingScreen messages={['Redirecting to setup...']} />;
    }

    // Handle error state
    if (isError) {
      flashbarItems.push({
        type: 'error',
        header: 'Error',
        content: 'An error occurred while loading user data.',
        id: 'error',
      });
    }

    if (flashbarItems.length > 0) {
      return <Flashbar items={flashbarItems} />;
    }

    return (
      <div>
        <AppHeader
          onProfileSettingsOpen={() => {
            navigate(routePaths.PROFILE_PREFERENCES.path);
          }}
        />
        {showWhatsNew && (
          <WhatsNewBanner
            onDismiss={handleDismissWhatsNew}
            message={getWhatsNewMessage()}
          />
        )}
        <CopilotProvider
          businessUnitId={getUserBusinessUnitId() || ''}
          isAuthenticated={!!userAlias}
          userId={userAlias || ''}
        >
          <NavigationProvider>
            <SplitPanelProvider>
              <AppLayoutWrapper />
            </SplitPanelProvider>
          </NavigationProvider>
          {/* Hide the Astra launcher and panel on focused, full-page flows (see
              COPILOT_HIDDEN_ROUTES) where the floating panel would overlap the task.
              The provider stays mounted so any in-progress conversation is preserved
              and restored when the user navigates to a route where Astra is shown. */}
          {!isCopilotHiddenOnRoute(location.pathname) && (
            <>
              <CopilotTrigger isAuthenticated={!!userAlias} />
              <CopilotPanel />
            </>
          )}
        </CopilotProvider>
      </div>
    );
  };

  return <div>{renderContent()}</div>;
};

// Main App component that provides the Redux context
const App = () => {
  return (
    <Provider store={store}>
      <I18nProvider locale={LOCALE} messages={[messages]}>
        <BrowserRouter>
          <AuthHandler>
            <QueryClientProvider client={queryClient}>
              <UserProvider>
                <AppContent />
              </UserProvider>
            </QueryClientProvider>
          </AuthHandler>
        </BrowserRouter>
      </I18nProvider>
    </Provider>
  );
};

export default App;
